// Roller-ski route planning via BRouter (brouter.de) — free, no API key, permissive
// CORS. BRouter returns a GeoJSON LineString with per-node elevation and a
// `messages` table of way tags (surface=…, highway=…). We route on a bike-ish
// asphalt profile (never routes onto motorways) and then analyse the elevation
// profile to flag downhills that are too steep and any non-asphalt / gravel bits.

export interface RoutePoint { lng: number; lat: number; ele: number; }
export type SegStatus = "ok" | "steep" | "surface" | "motorway";
export interface RouteSegment {
  a: RoutePoint;
  b: RoutePoint;
  distM: number;
  grade: number; // % (negative = downhill in travel direction)
  surface: string;
  highway: string;
  status: SegStatus;
}
export interface SteepSpot { lat: number; lng: number; grade: number; lengthM: number; maxGrade: number; dropM: number; }
// A hazard right after a descent — the key rollerski problem (poor braking →
// you carry speed into it): either a sharp turn or a crossing/traffic signal.
export interface DangerSpot { lat: number; lng: number; grade: number; kind: "turn" | "crossing"; turn?: number; }
export interface RouteResult {
  points: RoutePoint[];
  segments: RouteSegment[];
  lengthKm: number;
  ascentM: number;
  descentM: number;
  steepSections: number;
  steepSpots: SteepSpot[];
  dangerSpots: DangerSpot[];
  sharpTurns: number;
  crossingHazards: number;
  gravelKm: number;
  motorwayKm: number;
}

const R = 6371000;
const haversine = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const BAD_SURFACE = /gravel|unpaved|ground|dirt|earth|grass|sand|pebble|cobble|compacted|fine_gravel|wood|mud|clay/i;
const OK_SURFACE = /asphalt|paved|concrete|chipseal|paving_stones|metal|sett/i;

export const SEG_COLOR: Record<SegStatus, string> = {
  ok: "#2f9e44",
  steep: "#e03131",
  surface: "#f08c00",
  motorway: "#ae3ec9",
};

// Available BRouter profiles that make sense for asphalt road skating.
export const PROFILES: { key: string; label: string }[] = [
  { key: "fastbike", label: "Snabbcykel (föredrar asfaltvägar)" },
  { key: "trekking", label: "Trekking (lugnare, mer cykelväg)" },
];

export async function fetchRollerRoute(
  pts: { lat: number; lng: number }[],
  maxDownhill: number,
  profile = "fastbike",
  nogos: { lat: number; lng: number; r: number }[] = [], // circular areas BRouter must route around
  minSteepM = 0, // steep sections shorter than this are treated as OK
): Promise<RouteResult> {
  if (pts.length < 2) throw new Error("Sätt ut minst två waypoints.");
  const lonlats = pts.map((p) => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`).join("|");
  const nogoParam = nogos.length
    ? `&nogos=${nogos.map((n) => `${n.lng.toFixed(6)},${n.lat.toFixed(6)},${Math.round(n.r)}`).join("|")}`
    : "";
  const url = `https://brouter.de/brouter?lonlats=${lonlats}&profile=${encodeURIComponent(profile)}${nogoParam}&alternativeidx=0&format=geojson`;

  let gj: any;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`BRouter svarade ${res.status}`);
    gj = await res.json();
  } catch (e: any) {
    throw new Error(`Kunde inte beräkna rutt (${e?.message || "nätverksfel"}). Prova igen eller flytta waypoints närmare vägar.`);
  }

  const feat = gj?.features?.[0];
  if (!feat?.geometry?.coordinates?.length) throw new Error("Ingen rutt hittades mellan punkterna.");

  const coords: number[][] = feat.geometry.coordinates;
  const points: RoutePoint[] = coords.map((c) => ({ lng: c[0], lat: c[1], ele: typeof c[2] === "number" ? c[2] : 0 }));

  // messages: [header, ...rows]; align way tags to the geometry by distance.
  const msgs: any[] = feat.properties?.messages || [];
  const header: string[] = msgs[0] || [];
  const di = header.indexOf("Distance");
  const wi = header.indexOf("WayTags");
  const ni = header.indexOf("NodeTags");
  const runs: { dist: number; surface: string; highway: string }[] = [];
  const crossingDists: number[] = []; // along-route metres to each crossing / traffic signal
  if (di >= 0 && wi >= 0) {
    let runCum = 0;
    for (let i = 1; i < msgs.length; i++) {
      const row = msgs[i];
      const tags = String(row[wi] || "");
      runs.push({
        dist: Number(row[di] || 0),
        surface: (tags.match(/surface=([^\s]+)/) || [])[1] || "",
        highway: (tags.match(/highway=([^\s]+)/) || [])[1] || "",
      });
      runCum += Number(row[di] || 0);
      const nt = ni >= 0 ? String(row[ni] || "") : "";
      if (/highway=crossing|crossing=|highway=traffic_signals|highway=give_way|highway=stop/i.test(nt)) crossingDists.push(runCum);
    }
  }

  return analyzeRoute(points, maxDownhill, runs, crossingDists, minSteepM);
}

// Analyse an elevation/geometry profile (BRouter-routed OR an imported GPX used
// exactly as-is). `runs`/`crossingDists` carry road surface + crossing data from
// BRouter; a GPX has none, so those flags are simply skipped for it.
export function analyzeRoute(
  points: RoutePoint[],
  maxDownhill: number,
  runs: { dist: number; surface: string; highway: string }[] = [],
  crossingDists: number[] = [],
  minSteepM = 0, // steep sections shorter than this are treated as OK
): RouteResult {
  const segments: RouteSegment[] = [];
  let runIdx = 0;
  let runRemaining = runs[0]?.dist ?? Infinity;
  let lengthM = 0, ascent = 0, descent = 0, gravelM = 0, motorwayM = 0, steepSections = 0;
  const steepSpots: SteepSpot[] = [];
  let runStart = -1; // segment index where the current steep run started
  // A steep run shorter than minSteepM is harmless (you roll through) — demote it.
  const finalizeSteepRun = (lastSteepSegIdx: number) => {
    if (runStart < 0) return;
    const sp = steepSpots[steepSpots.length - 1];
    if (minSteepM > 0 && sp.lengthM < minSteepM) {
      steepSpots.pop();
      steepSections--;
      for (let k = runStart; k <= lastSteepSegIdx; k++) segments[k].status = "ok";
    }
    runStart = -1;
  };

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const distM = haversine(a, b);
    lengthM += distM;
    const dz = b.ele - a.ele;
    if (dz > 0) ascent += dz; else descent += -dz;
    const grade = distM > 0.5 ? (dz / distM) * 100 : 0;
    const run = runs[Math.min(runIdx, runs.length - 1)] || { surface: "", highway: "" };
    const { surface, highway } = run;
    runRemaining -= distM;
    while (runRemaining <= 0 && runIdx < runs.length - 1) { runIdx++; runRemaining += runs[runIdx].dist; }

    let status: SegStatus = "ok";
    if (/motorway|trunk/i.test(highway)) status = "motorway";
    else if (dz < 0 && Math.abs(grade) > maxDownhill) status = "steep";
    else if (surface && BAD_SURFACE.test(surface) && !OK_SURFACE.test(surface)) status = "surface";

    segments.push({ a, b, distM, grade, surface, highway, status });
    const segIdx = segments.length - 1;
    if (status === "steep") {
      if (runStart < 0) { runStart = segIdx; steepSections++; steepSpots.push({ lat: a.lat, lng: a.lng, grade, lengthM: 0, maxGrade: grade, dropM: 0 }); }
      const sp = steepSpots[steepSpots.length - 1];
      sp.lengthM += distM;
      sp.dropM += Math.max(0, a.ele - b.ele);
      if (grade < sp.maxGrade) sp.maxGrade = grade;
    } else {
      finalizeSteepRun(segIdx - 1);
    }
    if (status === "surface") gravelM += distM;
    if (status === "motorway") motorwayM += distM;
  }
  finalizeSteepRun(segments.length - 1);

  // ── Sharp turn right after a descent (the dangerous combo on rollerskis) ──
  // Compare heading ~25 m before vs ~25 m after each vertex; flag when the turn
  // is sharp AND the approach was descending fast enough to carry speed.
  const d2r = Math.PI / 180;
  const bearing = (a: RoutePoint, b: RoutePoint): number => {
    const y = Math.sin((b.lng - a.lng) * d2r) * Math.cos(b.lat * d2r);
    const x = Math.cos(a.lat * d2r) * Math.sin(b.lat * d2r)
      - Math.sin(a.lat * d2r) * Math.cos(b.lat * d2r) * Math.cos((b.lng - a.lng) * d2r);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  };
  const angleDiff = (a: number, b: number): number => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };

  const cum: number[] = [0];
  for (let i = 1; i < points.length; i++) cum[i] = cum[i - 1] + haversine(points[i - 1], points[i]);

  const TURN_DEG = 40;        // heading change that counts as a real turn
  const WINDOW_M = 20;        // baseline for measuring heading
  const DESC_GRADE = 4;       // a descent of ≥4% starts building real speed
  const CONT_GRADE = 1;       // …and keeps carrying while still ≥1% downhill
  const MIN_DROP = 3;         // ignore trivial descents (<3 m total drop)
  const n = points.length;
  const segGrade = (i: number): number => {
    const d = cum[i + 1] - cum[i];
    return d > 0.5 ? ((points[i + 1].ele - points[i].ele) / d) * 100 : 0;
  };
  const headBack = (k: number, win: number): number | null => {
    let b = k; while (b > 0 && cum[k] - cum[b] < win) b--;
    return b === k ? null : bearing(points[b], points[k]);
  };
  const headFwd = (k: number, win: number): number | null => {
    let f = k; while (f < n - 1 && cum[f] - cum[k] < win) f++;
    return f === k ? null : bearing(points[k], points[f]);
  };
  const turnAt = (k: number): number => {
    const hb = headBack(k, WINDOW_M), hf = headFwd(k, WINDOW_M);
    return hb == null || hf == null ? 0 : angleDiff(hb, hf);
  };

  // The real hazard is whether you can slow down before a turn/crossing. A
  // descent builds speed; the faster/longer it is (bigger drop) the more run-out
  // you need. Flag any sharp turn or crossing from the descent's start until a
  // run-out distance (≈6 m per metre dropped, 25–150 m) past where it flattens.
  const dangerSpots: DangerSpot[] = [];
  let lastTurnDist = -Infinity;
  const flaggedCrossings = new Set<number>();
  let i = 0;
  while (i < n - 1) {
    if (segGrade(i) > -DESC_GRADE) { i++; continue; }
    let j = i, drop = 0;
    while (j < n - 1 && segGrade(j) <= -CONT_GRADE) { drop += Math.max(0, points[j].ele - points[j + 1].ele); j++; }
    if (drop >= MIN_DROP) {
      const descLen = cum[j] - cum[i];
      const avgGrade = descLen > 0 ? -(drop / descLen) * 100 : 0;
      const runOut = Math.min(150, Math.max(25, 6 * drop));
      const zoneEnd = cum[j] + runOut;
      // (A) a sharp single bend anywhere from the descent start through the run-out
      for (let k = i + 1; k < n - 1 && cum[k] <= zoneEnd; k++) {
        if (cum[k] - lastTurnDist < 40) continue;
        const t = turnAt(k);
        if (t >= TURN_DEG) {
          lastTurnDist = cum[k];
          dangerSpots.push({ lat: points[k].lat, lng: points[k].lng, grade: avgGrade, kind: "turn", turn: Math.round(t) });
        }
      }
      // (B) heading change vs the hill's direction at the bottom — catches a turn
      // that's spread over several metres just after a long descent.
      const descHead = headBack(j, 25);
      if (descHead != null) {
        for (let k = j; k < n - 1 && cum[k] - cum[j] <= runOut; k++) {
          const hf = headFwd(k, 20);
          if (hf == null) continue;
          const dev = angleDiff(descHead, hf);
          if (dev >= TURN_DEG) {
            if (cum[k] - lastTurnDist >= 40) {
              lastTurnDist = cum[k];
              dangerSpots.push({ lat: points[k].lat, lng: points[k].lng, grade: avgGrade, kind: "turn", turn: Math.round(dev) });
            }
            break;
          }
        }
      }
      for (const cd of crossingDists) {
        if (cd < cum[i] - 5 || cd > zoneEnd || flaggedCrossings.has(cd)) continue;
        flaggedCrossings.add(cd);
        let k = 0; while (k < n - 1 && cum[k] < cd) k++;
        dangerSpots.push({ lat: points[k].lat, lng: points[k].lng, grade: avgGrade, kind: "crossing" });
      }
    }
    i = j + 1;
  }

  return {
    points, segments,
    lengthKm: lengthM / 1000,
    ascentM: Math.round(ascent),
    descentM: Math.round(descent),
    steepSections,
    steepSpots,
    dangerSpots,
    sharpTurns: dangerSpots.filter((d) => d.kind === "turn").length,
    crossingHazards: dangerSpots.filter((d) => d.kind === "crossing").length,
    gravelKm: gravelM / 1000,
    motorwayKm: motorwayM / 1000,
  };
}
